import React from "react";
import { useCan, useHasFeature } from "@/hooks/useCan";
// 例で参照する OPTION_CODES: import { OPTION_CODES } from "@/constants/optionCodes";

interface CanProps {
  /** 必要な権限アクション。配列なら全て満たす必要あり（例: "property:delete"） */
  action?: string | string[];
  /** 必要な契約オプションの code（機能ゲート）。配列なら全て必要（例: OPTION_CODES.ESTIMATE_AI）。
   *  日本語表示名でのベタ照合は禁止。必ず OPTION_CODES(code) を渡す。 */
  feature?: string | string[];
  /** 満たさない時に代わりに描画するもの（既定: 何も出さない） */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * 権限（ロール）と契約オプション（機能ゲート）に基づいて children を出し分ける宣言的ガード。
 * ページごとに `{can("x") && (...)}` を書く代わりに、これ1つで統一する。
 *
 *   <Can action="property:delete"><DeleteButton/></Can>
 *   <Can feature={OPTION_CODES.ESTIMATE_AI}><EstimateSection/></Can>
 *   <Can action="repair:edit" feature={OPTION_CODES.ESTIMATE_AI}>...</Can>   // 両方満たす時だけ
 *
 * ※ 防御の本体は API 側。これは見た目の出し分けのみ。
 */
export const Can: React.FC<CanProps> = ({
  action,
  feature,
  fallback = null,
  children,
}) => {
  const can = useCan();
  const hasFeature = useHasFeature();

  const actions = action ? (Array.isArray(action) ? action : [action]) : [];
  const features = feature ? (Array.isArray(feature) ? feature : [feature]) : [];

  const allowed =
    actions.every((a) => can(a)) && features.every((f) => hasFeature(f));

  return <>{allowed ? children : fallback}</>;
};
